import { FC, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Organization } from '@kaizenplatform/intelligence-entity';
import SpeculationRule from '../../../../entity/SpeculationRule';
import ReportContent from './ReportContent';
import { useLocation } from 'react-router-dom';
import { parse } from 'qs';
import { DatePicker } from '@material-ui/pickers';
import { TextField, Typography } from '@material-ui/core';
import styled from '@emotion/styled';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';
import { createPath } from 'history';
import mergeUrlQuery from '../../../../utility/mergeUrlQuery';
import StyledLoading from './StyledLoading';
import ReportEmpty from './ReportEmpty';

type Props = {
  organization: Organization | null;
  speculationRule: SpeculationRule | null;
  speculationRuleLoading: boolean;
};

const decideDate = ({
  paramStr,
  minDate,
  maxDate,
  defaultDate,
}: {
  paramStr?: string;
  minDate: DateTime;
  maxDate: DateTime;
  defaultDate: DateTime;
}): DateTime => {
  let date = defaultDate;
  if (paramStr) {
    const decidedDate = DateTime.fromISO(paramStr);
    if (decidedDate.isValid) {
      date = decidedDate;
    }
  }
  if (minDate.diff(date, 'days').days > 0) date = minDate;
  if (maxDate.diff(date, 'days').days < 0) date = maxDate;
  return date;
};

const LinkPrefetchReportTab: FC<Props> = ({ organization, speculationRule, speculationRuleLoading }) => {
  const location = useLocation();
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [minDate, setMinDate] = useState<DateTime | null>(null);
  const [maxDate, setMaxDate] = useState<DateTime | null>(null);
  const [fromDate, setFromDate] = useState<DateTime | null>(null);

  useEffect(() => {
    if (!speculationRule || !speculationRule.dpActivatedAt) return;

    const queryParams = parse(window.location.search.slice(1)) || {};
    const toParam = queryParams['to'] ? String(queryParams['to']) : undefined;
    const fromParam = queryParams['from'] ? String(queryParams['from']) : undefined;

    const minDate = speculationRule.dpActivatedAt.startOf('day');
    const maxDate = DateTime.local().plus({ days: -1 }).startOf('day');
    setMinDate(minDate);
    setMaxDate(maxDate);

    const _toDate = decideDate({ paramStr: toParam, defaultDate: maxDate, minDate, maxDate });
    const _fromDate = decideDate({ paramStr: fromParam, defaultDate: maxDate.plus({ days: -30 }), minDate, maxDate });
    setToDate(_toDate);
    setFromDate(_fromDate);
  }, [speculationRule, location]);

  if (!organization || !speculationRule || speculationRuleLoading) {
    return <StyledLoading />;
  }
  if (speculationRule.dpState !== 'active' || !speculationRule.dpActivatedAt) {
    return <ReportEmpty />;
  }
  if (!fromDate || !toDate || !minDate || !maxDate) {
    return <StyledLoading />;
  }

  const _setFromDate = (date: Date | DateTime | null) => {
    if (!date) return;
    let newFromDate: DateTime = date instanceof Date ? DateTime.fromJSDate(date) : date.startOf('day');

    if (toDate && toDate.diff(newFromDate, 'days').days < 0) {
      // toDate < fromDate になるので入れ替える
      const oldToDate = toDate;
      _setToDate(newFromDate);
      newFromDate = oldToDate;
    }
    setFromDate(newFromDate);

    const queryParams = parse(window.location.search.slice(1)) || {};
    queryParams['from'] = newFromDate.toFormat('yyyyMMdd');
    const url = new URL(createPath(mergeUrlQuery(location, { ...queryParams })), window.location.origin);
    window.history.replaceState(null, '', url);
  };

  const _setToDate = (date: Date | DateTime | null) => {
    if (!date) return;
    let newToDate: DateTime = date instanceof Date ? DateTime.fromJSDate(date) : date.startOf('day');

    if (fromDate && newToDate.diff(fromDate, 'days').days < 0) {
      // toDate < fromDate になるので入れ替える
      const oldFromDate = fromDate;
      _setFromDate(newToDate);
      newToDate = oldFromDate;
    }
    setToDate(newToDate);

    const queryParams = parse(window.location.search.slice(1)) || {};
    queryParams['to'] = newToDate.toFormat('yyyyMMdd');
    const url = new URL(createPath(mergeUrlQuery(location, { ...queryParams })), window.location.origin);
    window.history.replaceState(null, '', url);
  };

  return (
    <Container>
      <ReportHeader>
        <ReportTitle variant="h6">レポート</ReportTitle>
        <DateSection>
          <DatePicker
            inputFormat="yyyy/MM/dd"
            value={fromDate.toISODate()}
            minDate={minDate.toISODate()}
            maxDate={maxDate.toISODate()}
            onChange={_setFromDate}
            renderInput={(props) => <StyledTextField size="small" {...props} />}
          />
          <Typography style={{ margin: '0 8px' }}>〜</Typography>
          <DatePicker
            inputFormat="yyyy/MM/dd"
            value={toDate.toISODate()}
            minDate={minDate.toISODate()}
            maxDate={maxDate.toISODate()}
            onChange={_setToDate}
            renderInput={(props) => <StyledTextField size="small" {...props} />}
          />
        </DateSection>
      </ReportHeader>
      <ReportContent
        organization={organization}
        speculationRule={speculationRule}
        fromDate={fromDate}
        toDate={toDate}
      />
    </Container>
  );
};

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const ReportHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '24px',
  paddingBottom: '12px',
  borderBottom: '1px solid #e0e0e0',
});

const ReportTitle = styled(Typography)({
  fontWeight: 700,
  color: COLOR_TEXT_MAIN,
  fontSize: '18px',
});

const DateSection = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '320px',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
  },
});

export default LinkPrefetchReportTab;
